import { DestroyRef, Injectable, effect, inject, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom, timeout } from 'rxjs';
import { AppErrorKind, toAppError } from '@core/errors/app-error';
import { AdminAuthApi } from './admin-auth.api';
import { AuthSessionStore } from './auth-session.store';
import { LOGIN_PATH } from './auth-paths';
import { SessionExpiredHandler } from './session-expired.handler';

/** Refresh this long before the access token expires (halved for very short tokens). */
export const REFRESH_LEAD_MS = 60_000;
/** After a transient refresh failure, try again this much later while the access token still works. */
export const REFRESH_RETRY_MS = 15_000;
const LOGOUT_TIMEOUT_MS = 5_000;
const TRANSIENT_KINDS: readonly AppErrorKind[] = ['network', 'unavailable', 'server', 'throttled'];

/**
 * Keeps the admin session alive and ends it:
 * - schedules one silent `refreshAdminToken` shortly before the access token expires;
 * - shares a single in-flight refresh between callers (timer, route guard), so refreshes never storm;
 * - a rejected refresh ends the session through `SessionExpiredHandler` (toast + /login);
 * - `signOut()` calls `logoutAdmin` (best effort), then clears the session and opens /login.
 */
@Injectable({ providedIn: 'root' })
export class AdminSessionService {
  private readonly api = inject(AdminAuthApi);
  private readonly session = inject(AuthSessionStore);
  private readonly sessionExpired = inject(SessionExpiredHandler);
  private readonly router = inject(Router);

  private timer: ReturnType<typeof setTimeout> | null = null;
  private inFlight: Promise<boolean> | null = null;

  constructor() {
    effect(() => {
      const expiresAt = this.session.accessTokenExpiresAt();
      untracked(() => this.schedule(expiresAt));
    });
    inject(DestroyRef).onDestroy(() => this.cancelTimer());
  }

  /** For guards: true when the access token works, or after a successful refresh. */
  ensureSession(): boolean | Promise<boolean> {
    if (this.session.isAuthenticated()) return true;
    if (!this.session.canRefresh()) return false;
    return this.refresh();
  }

  /** Rotates the token pair. Concurrent callers share one request. Resolves false when the session could not be renewed. */
  refresh(): Promise<boolean> {
    this.inFlight ??= this.runRefresh().finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  async signOut(): Promise<void> {
    this.cancelTimer();
    if (this.session.isAuthenticated()) {
      try {
        await firstValueFrom(
          this.api.logout(this.session.refreshToken()).pipe(timeout(LOGOUT_TIMEOUT_MS)),
        );
      } catch {
        // Best effort: the local session ends either way.
      }
    }
    this.session.clear();
    await this.router.navigateByUrl(LOGIN_PATH);
  }

  private async runRefresh(): Promise<boolean> {
    const refreshToken = this.session.refreshToken();
    const generation = this.session.generation();
    if (!refreshToken || !this.session.canRefresh()) {
      this.endSession();
      return false;
    }
    try {
      const pair = await firstValueFrom(this.api.refresh(refreshToken));
      // Signed out (or another sign-in) while the request was running: drop the answer.
      if (generation !== this.session.generation()) return false;
      this.session.start(pair);
      return true;
    } catch (error: unknown) {
      if (generation !== this.session.generation()) return false;
      const kind = toAppError(error).kind;
      if (TRANSIENT_KINDS.includes(kind) && this.session.isAuthenticated()) {
        this.setTimer(REFRESH_RETRY_MS);
        return false;
      }
      this.endSession();
      return false;
    }
  }

  private schedule(expiresAt: number | null): void {
    this.cancelTimer();
    if (expiresAt === null) return;
    const remaining = expiresAt - Date.now();
    const delay = remaining > 2 * REFRESH_LEAD_MS ? remaining - REFRESH_LEAD_MS : remaining / 2;
    this.setTimer(Math.max(0, delay));
  }

  private setTimer(delay: number): void {
    this.cancelTimer();
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.refresh();
    }, delay);
  }

  private cancelTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private endSession(): void {
    this.cancelTimer();
    this.sessionExpired.handle();
  }
}
